import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { CenterPanel } from "@/components/layout/center-panel";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTenant } from "@/context/tenant-context";
import { useAgentsByCompany } from "@/hooks/use-agents";
import { issuesApi } from "@/api/issues";
import { queryKeys } from "@/lib/query-keys";
import { ArrowLeft } from "lucide-react";

const statuses = ["backlog", "todo", "in_progress", "in_review", "blocked", "done", "cancelled"] as const;
const priorities = ["low", "medium", "high", "urgent"] as const;

export function IssueDetail({
  issueId: embeddedIssueId,
  onBack,
}: {
  issueId?: string;
  onBack?: () => void;
} = {}) {
  const { issueId: routeIssueId } = useParams<{ issueId: string }>();
  const issueId = embeddedIssueId ?? routeIssueId;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();
  const { data: agents = [] } = useAgentsByCompany(tenantId);

  const { data: issue, isLoading } = useQuery({
    queryKey: queryKeys.issues.detail(issueId!),
    queryFn: () => issuesApi.get(issueId!),
    enabled: !!issueId,
  });

  const updateMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => issuesApi.update(issueId!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.issues.detail(issueId!) });
      queryClient.invalidateQueries({ queryKey: queryKeys.issues.byCompany(tenantId) });
    },
  });

  if (isLoading || !issue) {
    return <CenterPanel title="Issue"><p className="text-sm text-text-muted">Loading...</p></CenterPanel>;
  }

  const assignee = agents.find((a) => a.id === issue.assigneeAgentId);

  return (
    <CenterPanel>
      <Button
        variant="ghost"
        size="sm"
        className="mb-4"
        onClick={() => {
          if (onBack) {
            onBack();
            return;
          }
          navigate(`/tenants/${tenantId}/issues`);
        }}
      >
        <ArrowLeft size={14} className="mr-1" /> Back to Issues
      </Button>

      <h1 className="text-xl font-semibold text-text-primary mb-2">{issue.title}</h1>

      {issue.description && (
        <p className="text-sm text-text-secondary mb-6 whitespace-pre-wrap">{issue.description}</p>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Card>
          <CardHeader><CardTitle className="text-sm">Status</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-1.5">
              {statuses.map((s) => (
                <Button
                  key={s}
                  size="sm"
                  variant={issue.status === s ? "default" : "outline"}
                  onClick={() => updateMutation.mutate({ status: s })}
                  className="text-xs capitalize"
                >
                  {s.replace("_", " ")}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-sm">Priority</CardTitle></CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-1.5">
              {priorities.map((p) => (
                <Button
                  key={p}
                  size="sm"
                  variant={issue.priority === p ? "default" : "outline"}
                  onClick={() => updateMutation.mutate({ priority: p })}
                  className="text-xs capitalize"
                >
                  {p}
                </Button>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-sm">Assignee</CardTitle></CardHeader>
          <CardContent>
            <select
              value={issue.assigneeAgentId || ""}
              onChange={(e) => updateMutation.mutate({ assigneeAgentId: e.target.value || null })}
              className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
            >
              <option value="">Unassigned</option>
              {agents.map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="text-sm">Timestamps</CardTitle></CardHeader>
          <CardContent className="space-y-1 text-xs text-text-muted">
            <p>Created: {new Date(issue.createdAt).toLocaleString()}</p>
            {issue.startedAt && <p>Started: {new Date(issue.startedAt).toLocaleString()}</p>}
            {issue.completedAt && <p>Completed: {new Date(issue.completedAt).toLocaleString()}</p>}
          </CardContent>
        </Card>
      </div>
    </CenterPanel>
  );
}
